import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { barsApi } from '@/api/endpoints/bars';
import { specialsApi } from '@/api/endpoints/specials';
import { SEOHead } from '@/components/seo';
import { PageSpinner, Button, LocationPermissionPrompt, LocationErrorPrompt, NoAreaCoverage } from '@/components/ui';
import { Clock } from 'lucide-react';
import { useFilters, metersToMiles } from '@/contexts/FilterContext';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { useAreaCoverage } from '@/hooks/useAreaCoverage';
import { isActiveOnDate } from '@/lib/rrule';
import { getLocationSEO } from '@/lib/seoUtils';
import { HappyHourBarCard } from '../components/HappyHourBarCard';
import type { HappyHourBarEntry } from '../utils/happyHoursPageUtils';

const ITEMS_PER_PAGE = 20;

/**
 * Format a time string (HH:MM) to display format (e.g., "3pm", "10:30am")
 */
function formatHHTime(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const mins = m > 0 ? `:${m.toString().padStart(2, '0')}` : '';
  if (h === 0) return `12${mins}am`;
  if (h === 12) return `12${mins}pm`;
  return h > 12 ? `${h - 12}${mins}pm` : `${h}${mins}am`;
}

export function HappyHoursPage() {
  const { date, lat, lng, radius, hasValidLocation, locationStatus, locationLabel, locationSource } = useFilters();
  const { hidden } = useUserPreferences();
  const { hasBarCoverage, isCheckingCoverage } = useAreaCoverage();
  const [displayCount, setDisplayCount] = useState(ITEMS_PER_PAGE);

  // Fetch bars (for logos and distance) - single API call
  // Use distinct query key to avoid conflict with BarsPage's useInfiniteQuery
  const barsQuery = useQuery({
    queryKey: ['happy-hours-bars', { lat, lng, radius }],
    queryFn: () => barsApi.list({ lat: lat!, lng: lng!, radius, limit: 200 }),
    enabled: hasValidLocation,
  });

  // Fetch specials (for happy hour data) - single API call
  const specialsQuery = useQuery({
    queryKey: ['specials', { lat, lng, radius }],
    queryFn: () => specialsApi.list({ lat: lat!, lng: lng!, radius, limit: 500 }),
    enabled: hasValidLocation,
  });

  // Combine bars and specials client-side into happy hour entries
  const happyHourEntries = useMemo(() => {
    const bars = barsQuery.data?.bars ?? [];
    const specials = specialsQuery.data?.specials ?? [];

    if (bars.length === 0 || specials.length === 0) return [];

    // Create a map of bar data for quick lookup (excluding hidden bars)
    const barMap = new Map(
      bars.filter((b) => !hidden.has(b.id)).map((b) => [b.id, b])
    );

    // Filter to happy hour specials active on selected date
    const hhSpecials = specials.filter(
      (s) => s.isHappyHour && isActiveOnDate(s.rrule, date)
    );

    // Group by barId + time window
    const groups = new Map<string, {
      barId: string;
      barName: string;
      barLogo: string | null;
      distance: number | undefined;
      timeKey: string;
      specials: typeof hhSpecials;
    }>();

    for (const special of hhSpecials) {
      const bar = barMap.get(special.barId!);
      if (!bar) continue; // Skip if bar not in our radius

      const start = special.happyHourStart || 'open'; // null means "from open"
      const end = special.happyHourEnd || 'close'; // null means "until close"
      const timeKey = `${start}-${end}`;
      const groupKey = `${special.barId}|${timeKey}`;

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          barId: special.barId!,
          barName: bar.name,
          barLogo: bar.logoUrl,
          distance: bar.distance,
          timeKey,
          specials: [],
        });
      }
      groups.get(groupKey)!.specials.push(special);
    }

    // Convert to array with display time and sort by distance
    const entries: HappyHourBarEntry[] = Array.from(groups.values()).map((group) => {
      const [start, end] = group.timeKey.split('-');
      return {
        ...group,
        displayTime:
          start === 'open' && end === 'close'
            ? 'Open - Close'
            : start === 'open'
              ? `Open - ${formatHHTime(end)}`
              : end === 'close'
                ? `${formatHHTime(start)} - Close`
                : `${formatHHTime(start)} - ${formatHHTime(end)}`,
      };
    });

    return entries.sort((a, b) => {
      const distDiff = (a.distance ?? Infinity) - (b.distance ?? Infinity);
      if (distDiff !== 0) return distDiff;
      return a.timeKey.localeCompare(b.timeKey);
    });
  }, [barsQuery.data, specialsQuery.data, date, hidden]);

  // Pagination
  const displayedEntries = happyHourEntries.slice(0, displayCount);
  const hasMore = displayCount < happyHourEntries.length;

  const handleLoadMore = () => {
    setDisplayCount((prev) => prev + ITEMS_PER_PAGE);
  };

  // Loading states
  const isLoading = barsQuery.isLoading || specialsQuery.isLoading;

  // Location guards
  if (!hasValidLocation) {
    if (locationStatus === 'denied') return <LocationPermissionPrompt />;
    if (locationStatus === 'unavailable') return <LocationErrorPrompt />;
    return <PageSpinner />;
  }

  if (isCheckingCoverage || isLoading) return <PageSpinner />;
  if (!hasBarCoverage) return <NoAreaCoverage />;

  if (barsQuery.isError || specialsQuery.isError) {
    return (
      <div className="py-12 text-center">
        <p className="text-[var(--color-muted)]">Failed to load happy hours</p>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        {...getLocationSEO(
          'Happy Hour Deals Near You',
          `Find ${happyHourEntries.length} happy hour specials near you. Discounted drinks, food deals, and more from local bars.`,
          '/happy-hours',
          locationLabel,
          locationSource
        )}
      />
      <div className="space-y-4">
        {displayedEntries.length === 0 ? (
        <div className="py-12 text-center">
          <Clock className="mx-auto h-12 w-12 text-[var(--color-muted)] opacity-50" />
          <p className="mt-4 text-[var(--color-muted)]">
            No happy hours found within {metersToMiles(radius)} miles for this date
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3">
            {displayedEntries.map((entry) => (
              <HappyHourBarCard
                key={`${entry.barId}-${entry.timeKey}`}
                entry={entry}
              />
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center pt-4">
              <Button variant="ghost" onClick={handleLoadMore}>
                Load More
              </Button>
            </div>
          )}
        </>
      )}
      </div>
    </>
  );
}
